var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { concatStyleSets } from "@fluentui/merge-styles";
import { Stack, useTheme } from "@fluentui/react";
import React, { useEffect, useRef, useState } from "react";
import { Measures } from "../../../../telemetry/Measures";
import { dashboard } from "../../../../telemetry/TraceLogging";
import { addAutohideScrollbarsSettingChangedEventListener, getIsAutohideScrollbarsEnabled, removeAutohideScrollbarsSettingChangedEventListener } from "../../../externalApi/ThemeApi";
import { addVisibilityEventListeners, removeVisibilityEventListeners } from "../../../externalApi/WindowApi";
import { getBaseScrollableStackStyle } from "./ScrollableStack.styles";
export var ScrollableTelemetryIdentifier;
(function (ScrollableTelemetryIdentifier) {
    ScrollableTelemetryIdentifier[ScrollableTelemetryIdentifier["WidgetsAndFeed"] = 0] = "WidgetsAndFeed";
    ScrollableTelemetryIdentifier[ScrollableTelemetryIdentifier["WidgetPickerItems"] = 1] = "WidgetPickerItems";
})(ScrollableTelemetryIdentifier || (ScrollableTelemetryIdentifier = {}));
// The ScrollableStack component is a generic component that wraps the FluentUI Stack component, providing support for a consistently styled scrollbar on overflow
// as well as proper support for touch scrolling.
export const ScrollableStack = (_a) => {
    var { children, id, scrollableTelemetryIdentifier, isScrollbarForcedVisible, isScrollTelemetryEnabled, styles } = _a, rest = __rest(_a, ["children", "id", "scrollableTelemetryIdentifier", "isScrollbarForcedVisible", "isScrollTelemetryEnabled", "styles"]);
    const isScrollThrottleTimedOut = useRef(true);
    const scrollThrottleTimeout = useRef();
    const isScrollbarShownForTouchTimeout = useRef();
    const scrollInfo = useRef({
        positionOfLastScroll: 0,
        scrollDirection: -1 /* None */,
        scrollsDown: 0,
        scrollsUp: 0,
        sizeOfScrollRegion: 0,
    });
    const [isScrollbarShownForTouch, setIsScrollbarShownForTouch] = useState(false);
    useEffect(() => {
        function reportScrollTelemetry() {
            if (scrollInfo.current.scrollDirection !== -1 /* None */) {
                Measures.logEvent(dashboard, "WebAppScrollOccurred", 33554432 /* productAndServiceUsage */, {
                    id: id,
                    positionOfLastScroll: scrollInfo.current.positionOfLastScroll,
                    scrollDirection: scrollInfo.current.scrollDirection,
                    scrollableId: scrollableTelemetryIdentifier,
                    scrollsDown: scrollInfo.current.scrollsDown,
                    scrollsUp: scrollInfo.current.scrollsUp,
                    sizeOfScrollRegion: scrollInfo.current.sizeOfScrollRegion,
                });
                scrollInfo.current.scrollDirection = -1 /* None */;
                scrollInfo.current.positionOfLastScroll = 0;
                scrollInfo.current.scrollsDown = 0;
                scrollInfo.current.scrollsUp = 0;
            }
        }
        const shouldReportScrollTelemetry = isScrollTelemetryEnabled && scrollableTelemetryIdentifier !== undefined;
        if (shouldReportScrollTelemetry) {
            addVisibilityEventListeners(undefined, reportScrollTelemetry);
        }
        return function cleanup() {
            if (shouldReportScrollTelemetry) {
                // If ScrollableStack is unmounted, flush any recorded scroll telemetry before unmounting.
                reportScrollTelemetry();
                removeVisibilityEventListeners(undefined, reportScrollTelemetry);
            }
        };
    }, []);
    const [isAutohideScrollbarsEnabled, setIsAutohideScrollbarsEnabled] = useState(true);
    useEffect(() => {
        function updateIsAutohideScrollbarsEnabled() {
            getIsAutohideScrollbarsEnabled().then((result) => setIsAutohideScrollbarsEnabled(result));
        }
        updateIsAutohideScrollbarsEnabled();
        addAutohideScrollbarsSettingChangedEventListener(updateIsAutohideScrollbarsEnabled);
        return function cleanup() {
            removeAutohideScrollbarsSettingChangedEventListener(updateIsAutohideScrollbarsEnabled);
        };
    }, []);
    function onScrollHandler(e) {
        const callback = () => {
            isScrollThrottleTimedOut.current = true;
        };
        let priorScrollDirection = scrollInfo.current.scrollDirection;
        if (scrollInfo.current.positionOfLastScroll < e.currentTarget.scrollTop) {
            scrollInfo.current.scrollDirection = 1 /* Down */;
        }
        else if (scrollInfo.current.positionOfLastScroll > e.currentTarget.scrollTop) {
            scrollInfo.current.scrollDirection = 0 /* Up */;
        }
        scrollInfo.current.positionOfLastScroll = e.currentTarget.scrollTop;
        scrollInfo.current.sizeOfScrollRegion = e.currentTarget.scrollHeight;
        // Only counts scrolling when it changes directions/there's been a pause of at least 1s
        if (isScrollThrottleTimedOut.current || priorScrollDirection !== scrollInfo.current.scrollDirection) {
            if (scrollInfo.current.scrollDirection === 0 /* Up */) {
                scrollInfo.current.scrollsUp++;
            }
            else if (scrollInfo.current.scrollDirection === 1 /* Down */) {
                scrollInfo.current.scrollsDown++;
            }
        }
        if (scrollThrottleTimeout.current) {
            clearTimeout(scrollThrottleTimeout.current);
        }
        scrollThrottleTimeout.current = window.setTimeout(callback, 1000 /* 1s timeout to prevent capturing multiple scrolls during brief pauses */);
        isScrollThrottleTimedOut.current = false;
    }
    function startShowScrollbarForTouchWithTimeout() {
        if (isScrollbarShownForTouchTimeout.current) {
            clearTimeout(isScrollbarShownForTouchTimeout.current);
        }
        setIsScrollbarShownForTouch(true);
    }
    function stopShowScrollbarForTouchWithTimeout() {
        if (isScrollbarShownForTouchTimeout.current) {
            clearTimeout(isScrollbarShownForTouchTimeout.current);
        }
        isScrollbarShownForTouchTimeout.current = window.setTimeout(() => {
            setIsScrollbarShownForTouch(false);
        }, 500); // Makes scrollbar disappear after 500ms of touch scrolling ends.
    }
    const theme = useTheme();
    return (React.createElement(Stack, Object.assign({}, rest, { onScroll: isScrollTelemetryEnabled ? onScrollHandler : undefined, onTouchStart: startShowScrollbarForTouchWithTimeout, onTouchEnd: stopShowScrollbarForTouchWithTimeout, styles: concatStyleSets(getBaseScrollableStackStyle(theme, isScrollbarForcedVisible || isScrollbarShownForTouch, !isAutohideScrollbarsEnabled), styles) }), children));
};
